import { useCallback, useEffect, useRef } from 'react';
import { Divider, Popover, Stack, Typography } from '@mui/material';
import { CitationPopoverEntry, CitationPopoverProps } from './CitationPopover.props';
import CitationCard from '../../Cards/citation-card/CitationCard';

const CitationPopover = ({
  entries,
  columnarIdentifier,
  showProvenance = false,
  onDelete,
  onAccept,
  onReject,
  onInteractionActiveChange,
  onView,
  slotProps,
  ...popoverProps
}: CitationPopoverProps) => {
  const paperSlotProps = typeof slotProps?.paper === 'function' ? undefined : slotProps?.paper;
  const callerPaperSx = paperSlotProps?.sx;
  const activeInteractionsRef = useRef(new Set<React.Key>());

  const handleInteractionActiveChange = useCallback(
    (entryKey: React.Key, active: boolean) => {
      const wasActive = activeInteractionsRef.current.size > 0;
      if (active) {
        activeInteractionsRef.current.add(entryKey);
      } else {
        activeInteractionsRef.current.delete(entryKey);
      }
      const isActive = activeInteractionsRef.current.size > 0;
      if (isActive !== wasActive) {
        onInteractionActiveChange?.(isActive);
      }
    },
    [onInteractionActiveChange]
  );

  useEffect(
    () => () => {
      if (activeInteractionsRef.current.size > 0) {
        onInteractionActiveChange?.(false);
      }
    },
    [onInteractionActiveChange]
  );

  const aiEntries = entries.filter(entry => entry.aiCreated);
  const humanEntries = entries.filter(entry => !entry.aiCreated);

  const renderBlock = (
    title: string | null,
    blockEntries: CitationPopoverEntry[],
    testId: string,
    keyOffset: number,
    allowSettle: boolean
  ) => {
    if (blockEntries.length === 0) {
      return null;
    }
    return (
      <Stack key={testId} spacing={1} data-testid={testId}>
        {title ? <Typography variant="overline">{title}</Typography> : null}
        <Stack divider={<Divider flexItem />} spacing={2}>
          {blockEntries.map(({ citation, aiCreated }, index) => {
            const entryKey =
              citation.id ?? `${citation.document_reference}-${citation.field_name}-${keyOffset + index}`;
            return (
              <CitationCard
                key={entryKey}
                citation={citation}
                identifier={columnarIdentifier}
                aiCreated={showProvenance && aiCreated}
                onDelete={onDelete}
                onAccept={allowSettle ? onAccept : undefined}
                onReject={allowSettle ? onReject : undefined}
                onInteractionActiveChange={active => handleInteractionActiveChange(entryKey, active)}
                onView={onView}
              />
            );
          })}
        </Stack>
      </Stack>
    );
  };

  return (
    <Popover
      {...popoverProps}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      disableRestoreFocus
      slotProps={{
        ...slotProps,
        paper: {
          ...paperSlotProps,
          sx: [
            {
              maxWidth: '400px',
              maxHeight: '70vh',
              overflowY: 'auto',
              padding: 2,
            },
            ...(Array.isArray(callerPaperSx) ? callerPaperSx : [callerPaperSx]),
          ],
        },
      }}>
      <Stack divider={<Divider flexItem />} spacing={2}>
        {showProvenance ? (
          <>
            {renderBlock('AI citations', aiEntries, 'citation-block-ai', 0, true)}
            {renderBlock('Human citations', humanEntries, 'citation-block-human', aiEntries.length, false)}
          </>
        ) : (
          renderBlock(null, entries, 'citation-block-all', 0, false)
        )}
      </Stack>
    </Popover>
  );
};

export default CitationPopover;
